import i18next from 'i18next'
import posthog from 'posthog-js'
import * as mutations from 'src/graphql/mutations'
import * as queries from 'src/graphql/queries'
import { graphqlApi } from 'src/redux/api/graphql/graphqlApi'
import type { ReduxUserInfo } from 'src/types/apiType'
import { API, graphqlOperation } from 'src/utils/amplify/legacy'
import { getErrorMessage } from 'src/utils/error'
import { makeErrorToast, makeSuccessToast, msg } from 'src/utils/makeToast'
import { translateError } from 'src/utils/translateError'

export const userInfoApi = graphqlApi.injectEndpoints({
	endpoints: (builder) => ({
		fetchUserInfo: builder.query({
			query: ({ userInfoId }: { userInfoId: string }) => ({
				queryName: 'getUserInfo',
				data: {
					id: userInfoId,
				},
				isList: false,
			}),
			transformResponse: (response: ReduxUserInfo) => {
				return response
			},
			providesTags: (result) =>
				result?.id
					? [{ type: 'UserInfo', id: result.id }]
					: [{ type: 'UserInfo' }],
			keepUnusedDataFor: 300,
		}),
		fetchUserInfoByTenantId: builder.query({
			query: ({ tenantId }: { tenantId: string }) => ({
				queryName: 'listUserInfos',
				data: {
					filter: {
						or: [
							{
								currentCompanyTenantId: { eq: tenantId },
							},
							{
								companyTenantId: { contains: tenantId },
							},
						],
					},
					limit: 1000,
				},
				isList: true,
			}),
			transformResponse: (response: ReduxUserInfo[]) => {
				return response.filter((u) => {
					if (!u || !u.email) return false
					const email: string = u.email.toString().toLowerCase()
					return !email.endsWith('@chedar.fr')
				})
			},
			providesTags: (result) => [{ type: 'UserInfo', id: 'LIST' }],
			keepUnusedDataFor: 300,
		}),
		fetchUserCreationDatesByTenant: builder.query({
			query: ({ tenantId }: { tenantId: string }) => ({
				queryName: 'eventsByTenant',
				data: {
					tenantId,
					filter: { action: { eq: 'user.create' } },
					limit: 1000,
				},
				isList: true,
			}),
			// Build a map of userId -> timestamp of the `user.create` audit
			// event, i.e. when the user was added to this company. The earliest
			// event wins if duplicates exist. Consumers fall back to UserInfo
			// `createdAt` when a user has no audit event.
			transformResponse: (
				response:
					| {
							ts?: string | null
							entity?: { id?: string | null } | null
					  }[]
					| null
			) => {
				const creationDateByUserId: Record<string, string> = {}
				for (const event of response ?? []) {
					const userId = event?.entity?.id
					const ts = event?.ts
					if (!userId || !ts) continue
					if (
						!creationDateByUserId[userId] ||
						ts < creationDateByUserId[userId]
					) {
						creationDateByUserId[userId] = ts
					}
				}
				return creationDateByUserId
			},
			providesTags: () => [{ type: 'UserInfo', id: 'CREATION_DATES' }],
			keepUnusedDataFor: 300,
		}),
		updateCurrentCompanyUserInfo: builder.mutation({
			queryFn: async ({
				tenantId,
				name,
				userInfo,
			}: {
				tenantId: string
				name: string
				userInfo: ReduxUserInfo
			}) => {
				try {
					const {
						data: { getUserInfo: latestUserInfo },
					} = (await API.graphql(
						graphqlOperation(queries.getUserInfo, {
							id: userInfo.id,
						})
					)) as {
						data: { getUserInfo: ReduxUserInfo }
					}
					const updateInput: Partial<ReduxUserInfo> = {
						id: latestUserInfo.id,
						currentCompanyName: name,
						currentCompanyTenantId: tenantId,
					}

					const {
						data: { updateUserInfo: updatedUserInfo },
					} = (await API.graphql(
						graphqlOperation(mutations.updateUserInfo, {
							input: updateInput,
						})
					)) as {
						data: { updateUserInfo: ReduxUserInfo }
					}
					return { data: updatedUserInfo }
				} catch (error: any) {
					makeErrorToast(msg)
					return { error }
				}
			},
			async onQueryStarted(
				{ tenantId, name, userInfo },
				{ dispatch, queryFulfilled }
			) {
				const patchResult = dispatch(
					userInfoApi.util.updateQueryData(
						'fetchUserInfo',
						{ userInfoId: userInfo.id as string },
						(draft) => {
							if (draft && draft?.id === userInfo.id) {
								draft.currentCompanyName = name
								draft.currentCompanyTenantId = tenantId
							}
						}
					)
				)
				try {
					await queryFulfilled
				} catch {
					patchResult.undo()
				}
			},
			invalidatesTags: (result, error, { userInfo }) => [
				'Company',
				'Client',
				'Projects',
				'ProjectTotals',
				'PreparePurchase',
				'Quote',
				'QuoteTemplate',
				'BasicQuoteTemplate',
				'QuoteTotals',
				'QuoteInfo',
				'RecapInfo',
				'Suppliers',
				'UserInfo',
				'Freelancers',
				'FreelancerBilling',
				'Contact',
				'Comments',
				'SectionRename',
				'Currencies',
				'Purchase',
				'Proposals',
			],
		}),
		addNewUser: builder.mutation({
			queryFn: async (input: {
				email: string
				cognitoGroup: string
				tenantId: string
				teamIds?: string[]
				subTeamIds?: string[]
				projectIds?: string[]
			}) => {
				try {
					const { data } = (await API.graphql(
						graphqlOperation(mutations.newUserManagement, {
							...input,
						})
					)) as any
					if (data?.newUserManagement?.status === 'error') {
						const response = data.newUserManagement

						// Translate error if we have an error code
						const errorMessage = translateError(
							response.errorCode,
							response.message
						)

						makeErrorToast(errorMessage)

						// Log to PostHog without throwing
						posthog.captureException(new Error(errorMessage), {
							location: 'userInfoApi.addNewUser',
							requestedEmail: input.email,
							requestedTenantId: input.tenantId,
							cognitoGroup: input.cognitoGroup,
							errorCode: response.errorCode,
						})

						return {
							error: {
								message: errorMessage,
								errorCode: response.errorCode,
							},
						}
					}
					makeSuccessToast(
						i18next.t(
							`
							teamsettings:teamSettings.userAddedSuccess`,
							'User added successfully'
						)
					)
					return { data: null }
				} catch (error) {
					const errorMessage = getErrorMessage(error)
					makeErrorToast(errorMessage)
					return { error }
				}
			},
			invalidatesTags: () => [
				{ type: 'UserInfo', id: 'LIST' },
				{ type: 'Freelancers' },
			],
		}),
		createCompany: builder.mutation({
			queryFn: async (input: {
				companyName: string
				country: string
				currency: string
				idempotencyKey: string
			}) => {
				const createCompanyManagementMutation = /* GraphQL */ `
					mutation CreateCompanyManagement(
						$companyName: String!
						$country: String!
						$currency: String!
						$idempotencyKey: String!
					) {
						createCompanyManagement(
							companyName: $companyName
							country: $country
							currency: $currency
							idempotencyKey: $idempotencyKey
						) {
							status
							message
							tenantId
							companyName
							errorCode
						}
					}
				`
				try {
					const { data } = (await API.graphql(
						graphqlOperation(createCompanyManagementMutation, {
							...input,
						})
					)) as {
						data?: {
							createCompanyManagement?: {
								status?: string
								message?: string
								tenantId?: string
								companyName?: string
								errorCode?: string
							}
						}
					}
					const response = data?.createCompanyManagement
					if (!response) {
						return {
							error: {
								message: i18next.t(
									'apiError:account.company.createFailed',
									'Unable to create company. Please try again.'
								),
							},
						}
					}
					if (response.status === 'error') {
						return {
							error: {
								message: translateError(
									response.errorCode,
									response.message
								),
								errorCode: response.errorCode,
							},
						}
					}
					return {
						data: {
							tenantId: response.tenantId ?? '',
							companyName:
								response.companyName ?? input.companyName,
						},
					}
				} catch (error) {
					return {
						error: {
							message: getErrorMessage(error),
						},
					}
				}
			},
			invalidatesTags: () => [
				{ type: 'UserInfo', id: 'LIST' },
				{ type: 'Company' },
			],
		}),
		editUserGroup: builder.mutation({
			queryFn: async (input: {
				email: string
				cognitoGroup: string
				tenantId: string
				teamIds?: string[]
				subTeamIds?: string[]
			}) => {
				try {
					const { data } = (await API.graphql(
						graphqlOperation(mutations.editUserManagement, {
							...input,
						})
					)) as any
					if (data?.editUserManagement?.status === 'error') {
						const response = data.editUserManagement

						const errorMessage = translateError(
							response.errorCode,
							response.message
						)

						makeErrorToast(errorMessage)

						posthog.captureException(new Error(errorMessage), {
							location: 'userInfoApi.editUserGroup',
							requestedEmail: input.email,
							requestedTenantId: input.tenantId,
							cognitoGroup: input.cognitoGroup,
							errorCode: response.errorCode,
						})

						return {
							error: {
								message: errorMessage,
								errorCode: response.errorCode,
							},
						}
					}
					makeSuccessToast(
						i18next.t(
							`
							teamsettings:teamSettings.userUpdatedSuccess`,
							'User updated successfully'
						)
					)
					return { data: null }
				} catch (error) {
					const errorMessage = getErrorMessage(error)
					makeErrorToast(errorMessage)
					return { error }
				}
			},
			invalidatesTags: () => [{ type: 'UserInfo', id: 'LIST' }],
		}),
		deleteUser: builder.mutation({
			queryFn: async (input: { email: string; tenantId: string }) => {
				try {
					const { data } = (await API.graphql(
						graphqlOperation(mutations.deleteUser, {
							...input,
						})
					)) as any
					if (data?.deleteUser?.status === 'error') {
						const response = data.deleteUser

						// Translate error if we have an error code
						const errorMessage = translateError(
							response.errorCode,
							response.message
						)

						makeErrorToast(errorMessage)

						// Log to PostHog without throwing
						posthog.captureException(new Error(errorMessage), {
							location: 'userInfoApi.deleteUser',
							requestedEmail: input.email,
							requestedTenantId: input.tenantId,
							errorCode: response.errorCode,
						})

						return {
							error: {
								message: errorMessage,
								errorCode: response.errorCode,
							},
						}
					}
					makeSuccessToast(
						i18next.t(
							`
							teamsettings:teamSettings.userDeletedSuccess`,
							'User deleted successfully'
						)
					)
					return { data: null }
				} catch (error) {
					const errorMessage = getErrorMessage(error)
					makeErrorToast(errorMessage)
					return { error }
				}
			},
			invalidatesTags: () => [
				{ type: 'UserInfo', id: 'LIST' },
				{ type: 'Freelancers' },
			],
		}),
	}),
	overrideExisting: false,
})

export const {
	useFetchUserInfoQuery,
	useFetchUserInfoByTenantIdQuery,
	useFetchUserCreationDatesByTenantQuery,
	useUpdateCurrentCompanyUserInfoMutation,
	useAddNewUserMutation,
	useCreateCompanyMutation,
	useEditUserGroupMutation,
	useDeleteUserMutation,
} = userInfoApi
